// src/pages/Winners.jsx
import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaTrophy, FaMedal, FaStar, FaGift } from "react-icons/fa";
import Navbar from "../components/Navbar";

const BASE_URL = "https://api.dreamtoday.in";

const getAvatarUrl = (profile) => {
  if (!profile) return null;
  if (profile.startsWith("http")) return profile;
  return `${BASE_URL}${profile}`;
};

const Initials = ({ name }) => {
  const letters = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-400 to-amber-600 text-white font-bold text-2xl">
      {letters}
    </div>
  );
};

/* ── SKELETON ── */
const SkeletonCard = () => (
  <div className="bg-white rounded-3xl overflow-hidden shadow-md border border-yellow-100 animate-pulse">
    <div className="w-full h-52 bg-amber-100" />
    <div className="p-6 space-y-3">
      <div className="h-5 bg-amber-100 rounded w-2/3" />
      <div className="h-4 bg-amber-100 rounded w-1/2" />
      <div className="h-3 bg-amber-100 rounded w-full" />
      <div className="h-3 bg-amber-100 rounded w-3/4" />
    </div>
  </div>
);

const FeaturedSkeleton = () => (
  <div className="bg-gradient-to-r from-[#FFF3CC] to-[#FFE8A3] rounded-3xl p-10 shadow-xl border border-yellow-200 grid md:grid-cols-2 gap-10 items-center animate-pulse">
    <div className="w-full h-72 bg-amber-200 rounded-2xl" />
    <div className="space-y-4">
      <div className="h-4 bg-amber-200 rounded w-1/3" />
      <div className="h-8 bg-amber-200 rounded w-2/3" />
      <div className="h-5 bg-amber-200 rounded w-1/2" />
      <div className="h-4 bg-amber-200 rounded w-full" />
      <div className="h-3 bg-amber-200 rounded w-1/3" />
    </div>
  </div>
);

/* ── STATUS BADGE ── */
const StatusBadge = ({ status }) => {
  const cfg = {
    pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
    claimed: "bg-green-50 text-green-700 border-green-200",
    distributed: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return (
    <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${cfg[status] || cfg.pending}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1) || "Pending"}
    </span>
  );
};

/* ── WINNER CARD ── */
const WinnerCard = ({ winner, index }) => {
  const avatarUrl = getAvatarUrl(winner.winnerProfile);
  const [imgErr, setImgErr] = useState(false);

  return (
    <motion.div
      whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(251,191,36,0.18)" }}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.4 }}
      className="bg-white rounded-3xl overflow-hidden shadow-lg border border-yellow-100 flex flex-col"
    >
      {/* Avatar / Image */}
      <div className="w-full h-52 overflow-hidden relative bg-amber-50 flex items-center justify-center">
        {avatarUrl && !imgErr ? (
          <img
            src={avatarUrl}
            alt={winner.winnerName}
            className="w-full h-full object-cover"
            onError={() => setImgErr(true)}
          />
        ) : (
          <Initials name={winner.winnerName} />
        )}
        {/* Prize badge overlay */}
        <div className="absolute top-3 right-3">
          <StatusBadge status={winner.winnerStatus} />
        </div>
      </div>

      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-1">
          <FaTrophy className="text-yellow-500 text-sm shrink-0" />
          <h3 className="text-lg font-bold text-gray-900 truncate">{winner.winnerName}</h3>
        </div>

        <p className="text-yellow-700 font-semibold mb-1 text-sm flex items-center gap-1.5">
          <FaGift className="text-amber-400 shrink-0" />
          {winner.prizeName}
        </p>

        <p className="text-xs text-gray-400 mb-3">{winner.campaignTitle}</p>

        {winner.feedback && (
          <p className="text-sm text-gray-600 italic mb-3 line-clamp-2">
            "{winner.feedback}"
          </p>
        )}

        {winner.rating && (
          <div className="flex gap-0.5 mb-3">
            {[...Array(5)].map((_, i) => (
              <FaStar key={i} className={i < winner.rating ? "text-yellow-400" : "text-gray-200"} />
            ))}
          </div>
        )}

        <p className="text-xs text-gray-400 mt-auto">
          🎟 {winner.ticketIds.join(", ")} • {winner.bookingDate}
        </p>
      </div>
    </motion.div>
  );
};

/* ══════════════════════════════════════
   MAIN WINNERS PAGE
══════════════════════════════════════ */
const Winners = () => {
  const [search, setSearch] = useState("");
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchWinners = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/winning-gifts`);
        const data = await res.json();
        if (data.success) {
          setWinners(data.bookings || []);
        } else {
          setError(data.message || "Failed to fetch winners.");
        }
      } catch {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchWinners();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return winners;
    return winners.filter((w) =>
      w.winnerName.toLowerCase().includes(search.toLowerCase()) ||
      w.prizeName.toLowerCase().includes(search.toLowerCase()) ||
      w.campaignTitle.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, winners]);

  const featuredWinner = winners[0] || null;
  const featuredAvatarUrl = featuredWinner ? getAvatarUrl(featuredWinner.winnerProfile) : null;
  const [featuredImgErr, setFeaturedImgErr] = useState(false);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-white via-[#FFF7DB] to-white pt-28 pb-20 px-4">

        {/* HERO */}
        <div className="max-w-6xl mx-auto text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-600 via-yellow-700 to-yellow-800 bg-clip-text text-transparent"
          >
            Our Campaign Winners
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-600 max-w-2xl mx-auto"
          >
            Real people. Real prizes. Real dreams coming true.
          </motion.p>
          {!loading && !error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-4 inline-flex items-center gap-2 bg-amber-100 border border-yellow-300 text-amber-800 text-sm font-semibold px-5 py-2 rounded-full"
            >
              <FaMedal className="text-yellow-600" />
              {winners.length} Winner{winners.length !== 1 ? "s" : ""} and counting!
            </motion.div>
          )}
        </div>

        {/* SEARCH */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="flex items-center bg-white border border-yellow-300 rounded-2xl px-5 py-4 shadow-sm">
            <FaSearch className="text-yellow-700 mr-3 shrink-0" />
            <input
              type="text"
              placeholder="Search winners by name, prize or campaign..."
              className="w-full outline-none text-gray-700"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="max-w-xl mx-auto mb-12 text-center text-red-500 bg-red-50 border border-red-200 rounded-2xl px-6 py-5">
            ⚠️ {error}
          </div>
        )}

        {/* FEATURED WINNER */}
        <div className="max-w-6xl mx-auto mb-20">
          {loading ? (
            <FeaturedSkeleton />
          ) : featuredWinner ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-r from-[#FFF3CC] to-[#FFE8A3] rounded-3xl p-10 shadow-xl border border-yellow-200 grid md:grid-cols-2 gap-10 items-center"
            >
              {/* Avatar */}
              <div className="w-full h-72 rounded-2xl overflow-hidden flex items-center justify-center bg-amber-200">
                {featuredAvatarUrl && !featuredImgErr ? (
                  <img
                    src={featuredAvatarUrl}
                    alt={featuredWinner.winnerName}
                    className="w-full h-full object-cover"
                    onError={() => setFeaturedImgErr(true)}
                  />
                ) : (
                  <Initials name={featuredWinner.winnerName} />
                )}
              </div>

              <div>
                <div className="flex items-center gap-3 mb-4 text-yellow-700 font-semibold">
                  <FaTrophy /> Featured Winner
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  {featuredWinner.winnerName}
                </h2>

                <p className="text-xl font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                  <FaGift className="text-amber-500" /> Won {featuredWinner.prizeName}
                </p>

                {featuredWinner.feedback && (
                  <p className="text-gray-600 italic mb-4">
                    "{featuredWinner.feedback}"
                  </p>
                )}

                <div className="flex flex-wrap gap-3 mb-4">
                  <StatusBadge status={featuredWinner.winnerStatus} />
                  {featuredWinner.rating && (
                    <div className="flex gap-0.5 items-center">
                      {[...Array(5)].map((_, i) => (
                        <FaStar key={i} className={i < featuredWinner.rating ? "text-yellow-400" : "text-gray-300"} />
                      ))}
                    </div>
                  )}
                </div>

                <p className="text-sm text-gray-500">
                  {featuredWinner.campaignTitle} • {featuredWinner.bookingDate}
                </p>

                <p className="text-xs text-gray-400 mt-1">
                  Ticket: {featuredWinner.ticketIds.join(", ")}
                </p>
              </div>
            </motion.div>
          ) : null}
        </div>

        {/* WINNERS GRID */}
        {loading ? (
          <div className="max-w-7xl mx-auto grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : (
          <AnimatePresence>
            <div className="max-w-7xl mx-auto grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((winner, index) => (
                <WinnerCard key={winner.bookingId} winner={winner} index={index} />
              ))}
            </div>
          </AnimatePresence>
        )}

        {!loading && filtered.length === 0 && !error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mt-20 text-gray-500"
          >
            {search ? `No winners found for "${search}".` : "No winners yet."}
          </motion.p>
        )}
      </div>
    </>
  );
};

export default Winners;